import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { environment } from '../../../environments/environment';
import { CurrentUser } from '../types/auth.types';
import { currentUserIdOf } from '../utils/auth-roles';
import { scrubUrl } from '../observability/sentry-init';

type DiagnosticLevel = 'debug' | 'info' | 'warning' | 'error';

interface NetworkInformationLike {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

@Injectable({ providedIn: 'root' })
export class DiagnosticsService {
  addBreadcrumb(category: string, message: string, data?: Record<string, unknown>, level: DiagnosticLevel = 'info'): void {
    Sentry.addBreadcrumb({
      category,
      message,
      level,
      data: {
        ...data,
        online: navigator.onLine,
        swControlled: Boolean(navigator.serviceWorker?.controller),
      },
    });
  }

  captureHttpError(error: HttpErrorResponse, request: HttpRequest<unknown>, durationMs: number): void {
    const classification = this.classifyHttpError(error);
    const sanitizedUrl = scrubUrl(error.url || request.urlWithParams || request.url);
    const requestData = {
      method: request.method,
      url: sanitizedUrl,
      status: error.status,
      statusText: error.statusText,
      durationMs,
      classification,
      errorName: error.name,
      errorMessage: scrubUrl(error.message),
      online: navigator.onLine,
      swControlled: Boolean(navigator.serviceWorker?.controller),
    };

    this.addBreadcrumb('http', `${request.method} ${classification}`, requestData, error.status >= 500 ? 'error' : 'warning');

    // 4xx are expected application/API outcomes. Keep them as breadcrumbs so
    // they remain useful context for a later actionable event, but do not open
    // standalone Sentry issues for them.
    if (!shouldSendHttpErrorToSentry(error.status)) {
      return;
    }

    Sentry.withScope((scope) => {
      scope.setLevel(error.status >= 500 || error.status === 0 ? 'error' : 'warning');
      scope.setContext('http', requestData);
      scope.setContext('browser_state', this.browserState());
      scope.setFingerprint(['dashboard-http-error', request.method, String(error.status), classification, sanitizedUrl || request.url]);
      Sentry.captureException(new Error(`Dashboard HTTP ${classification}: ${request.method} ${error.status}`), {
        originalException: error,
      });
    });
  }

  setCurrentUser(user: CurrentUser | null): void {
    if (!user) {
      Sentry.setUser(null);
      return;
    }

    Sentry.setUser({
      id: currentUserIdOf(user) ?? undefined,
      email: user.email,
      role: user.role,
    });
  }

  setAuthContext(role?: string, businessStatus?: string, registrationStage?: string): void {
    Sentry.setContext('auth', {
      role,
      businessStatus,
      registrationStage,
    });
  }

  captureAppMessage(message: string, data?: Record<string, unknown>, level: DiagnosticLevel = 'info'): void {
    this.addBreadcrumb('app', message, data, level);
    Sentry.withScope((scope) => {
      scope.setLevel(level);
      if (data) {
        scope.setContext('app_event', data);
      }
      Sentry.captureMessage(message);
    });
  }

  flush(timeoutMs = 1500): Promise<boolean> {
    return Sentry.flush(timeoutMs);
  }

  private classifyHttpError(error: HttpErrorResponse): string {
    if (error.status === 0) {
      if (!navigator.onLine) {
        return 'offline';
      }
      if (error.error instanceof ProgressEvent) {
        return 'network_or_cors';
      }
      return 'client_network_error';
    }

    if (error.status === 401) {
      return 'unauthorized';
    }
    if (error.status === 403) {
      return 'forbidden';
    }
    if (error.status === 408 || error.status === 504) {
      return 'timeout';
    }
    if (error.status >= 500) {
      return 'server_error';
    }
    if (error.status >= 400) {
      return 'client_error';
    }
    return 'unknown';
  }

  private browserState(): Record<string, unknown> {
    const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
    return {
      production: environment.production,
      apiBaseUrl: environment.apiBaseUrl,
      userAgent: navigator.userAgent,
      language: navigator.language,
      online: navigator.onLine,
      visibilityState: document.visibilityState,
      swControlled: Boolean(navigator.serviceWorker?.controller),
      swControllerState: navigator.serviceWorker?.controller?.state,
      connection: connection ? {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData,
      } : null,
    };
  }
}

export function shouldSendHttpErrorToSentry(status: number): boolean {
  return status < 400 || status >= 500;
}
